import { DcrKeyPipe } from '@blk/cwp-ng-framework';
import { Alignment, ColumnComposition, ColumnCompositions, ContentProvider, ImageAndTextRenderer } from '@blk/simba';
import { CellData, ColumnConfig, ColumnText, TabData, TableData } from '@ddt-web/data-service';
import { EsgScoreImpactUtil } from '@ddt-web/esg';
import * as get from 'lodash/get';

import { SvgImage } from '../static-pages/static-section/svg-image';
import { TableCompositionHelper } from './table-composition-helper';

export class EsgScoreColumnComposition {

	protected textSize: string = 'text-8';
	protected boxModel = { paddingLeft: 5, paddingRight: 5 };

	public getColumnCompositions(
		tabData: TabData,
		tableColumnProportions: Array<number>,
		esgCarbonEquivalencyMap: Object,
		esgIndustryInvolvementMetrics: Array<string>,
		isIndustryInvolvement: boolean,
		contentProvider: ContentProvider,
		enableRoundOff: boolean = false
	): ColumnCompositions {
		const tableData: TableData = get(tabData, 'tableData[0]');
		const combinedCells: CellData[][] = [];
		tabData.tableData.forEach(table => {
			table.cells.forEach(row => {
				combinedCells.push(row);
			});
		});

		const tableHeaders: Array<CellData> = [...tableData.headersX];
		const columnProportions: Array<number> = tableColumnProportions;

		const esgMetrics = isIndustryInvolvement ? esgIndustryInvolvementMetrics : Object.keys(esgCarbonEquivalencyMap);

		const finalComposition = esgMetrics.map(metric => {
			const dcrKey = new DcrKeyPipe().transform('ESG_METRIC_' + metric + '_LABEL');
			const metricName = contentProvider.get(dcrKey, metric);
			let impactScoreCells = combinedCells[0];
			const benchmarkScoreCells = combinedCells[1];
			if (isIndustryInvolvement) {
				impactScoreCells = combinedCells.find(cell => cell[0]?.config?.id === metric);
			}
			return [
				new ColumnComposition({
					renderer: new ImageAndTextRenderer(
						new CellData(new ColumnConfig(new ColumnText(''), new ColumnText(''), null, null, null, null, true), metricName, metricName),
						new SvgImage(metric).getStaticContent(),
						Alignment.Horizontal.Left,
						columnProportions[0],
						[20, 80],
						[this.textSize],
						{},
						{ marginLeft: 10 }
					)
				}),
				...impactScoreCells.map(
					(cell: CellData, cellIndex: number) => {
						let equivalencyScore;
						if (isIndustryInvolvement) {
							equivalencyScore = cell?.formattedValue;
						} else {
							if (metric === 'CARBON_EMISSION_DIFFERENCE') {
								equivalencyScore = EsgScoreImpactUtil.getCarbonEmissionDifference(cell, benchmarkScoreCells[cellIndex]);
							}
							else {
								equivalencyScore = EsgScoreImpactUtil.getCarbonImpactScore(cell, metric, esgCarbonEquivalencyMap, enableRoundOff);
							}
						}
						const unitDcrKey = new DcrKeyPipe().transform('ESG_METRIC_' + metric + '_UNIT');
						const metricUnit = contentProvider.get(unitDcrKey, '');
						const metricToShow = equivalencyScore === '-' ? equivalencyScore : equivalencyScore + ' ' + metricUnit;

						return TableCompositionHelper.getColumnComposition(
							new CellData(new ColumnConfig(new ColumnText('Metric'), new ColumnText('Metric'), null, null, null, null, true), metricToShow, metricToShow),
							columnProportions[cellIndex + 1],
							[this.textSize],
							Alignment.Horizontal.Right,
							Alignment.Vertical.Middle,
							this.boxModel
						);
					}
				)];
		});

		return new ColumnCompositions(
			this.getHeaderRowCompositions(tableHeaders, columnProportions, contentProvider),
			finalComposition
		);
	}

	protected getHeaderRowCompositions(headers: Array<CellData>, columnProportions: Array<number>, contentProvider: ContentProvider): Array<ColumnComposition> {
		const impactLabel = contentProvider.get('ESG_IMPACT_LABEL', 'Impact');
		return [TableCompositionHelper.getColumnComposition(
			new CellData(new ColumnConfig(new ColumnText('Impact'), new ColumnText('Impact'), null), impactLabel, impactLabel),
			columnProportions[0],
			['text-bold', this.textSize],
			Alignment.Horizontal.Left,
			Alignment.Vertical.Bottom,
			this.boxModel
		), ...headers.map(
			(header: CellData, index: number) => TableCompositionHelper.getColumnComposition(
				header,
				columnProportions[index + 1],
				['text-bold', this.textSize],
				Alignment.Horizontal.Right,
				Alignment.Vertical.Bottom,
				this.boxModel
			)
		)];
	}
}
